import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "@/hooks/use-toast";
import { calculateNextServiceDate as calcNextService } from "@/lib/dateUtils";

export const calculateNextServiceDate = calcNextService;

export const useAppliances = (propertyId = null) => {
    const { user } = useAuth();
    const queryClient = useQueryClient();

    // 1. Fetching Logic (Query)
    const { data: appliances = [], isLoading: loading, refetch } = useQuery({
        queryKey: ["appliances", propertyId],
        queryFn: async () => {
            if (!user) return [];
            let query = supabase
                .from("appliances")
                .select("*")
                .order("created_at", { ascending: false });

            if (propertyId) {
                query = query.eq("property_id", propertyId);
            }

            const { data, error } = await query;
            if (error) throw error;
            return data || [];
        },
        enabled: !!user,
        staleTime: 1000 * 60 * 5, // Cache for 5 minutes
    });

    // 2. Addition Mutation
    const addMutation = useMutation({
        mutationFn: async (appliance) => {
            if (!user) return null;
            const baseDate = appliance.lastServiceDate || appliance.installationDate || null;
            const nextServiceDate = calculateNextServiceDate(
                baseDate,
                appliance.serviceFrequency || null,
                appliance.serviceFrequencyUnit || "months"
            );

            const { data, error } = await supabase.from("appliances").insert({
                user_id: user.id,
                name: appliance.name,
                brand: appliance.brand || null,
                category: appliance.category || null,
                installation_date: appliance.installationDate || null,
                last_service_date: appliance.lastServiceDate || appliance.installationDate || null,
                service_frequency: appliance.serviceFrequency || null,
                service_frequency_unit: appliance.serviceFrequencyUnit || "months",
                next_service_date: nextServiceDate,
                provider_id: appliance.providerId || null,
                warranty_expiry: appliance.warrantyExpiry || null,
                property_id: appliance.propertyId || propertyId || null,
                provider_notifications: appliance.providerNotifications ?? true,
                purchase_price: appliance.purchasePrice || null,
                purchase_date: appliance.purchaseDate || null,
                receipt_url: appliance.receiptUrl || null,
                // Fix: warranty document upload had nowhere to write its URL —
                // AddApplianceSheet.jsx now collects warrantyUrl but this insert
                // never persisted it.
                warranty_url: appliance.warrantyUrl || null,
                maintenance_tasks: appliance.maintenanceTasks || [],
            }).select().single();

            if (error) throw error;
            return data;
        },
        onSuccess: (data) => {
            toast({
                title: "Appliance Added",
                description: `${data.brand || 'Appliance'} ${data.name} has been added to your vault.`,
            });
            queryClient.invalidateQueries({ queryKey: ["appliances"] });
        },
        onError: (error) => {
            toast({
                title: "Error",
                description: `Failed to add appliance: ${error.message}`,
                variant: "destructive",
            });
        }
    });

    // 3. Update Mutation
    const updateMutation = useMutation({
        mutationFn: async ({ id, updates }) => {
            const currentAppliance = appliances.find(a => a.id === id);
            const baseDate = updates.lastServiceDate || currentAppliance?.last_service_date || currentAppliance?.installation_date;
            const freq = updates.serviceFrequency !== undefined ? updates.serviceFrequency : currentAppliance?.service_frequency;
            const freqUnit = updates.serviceFrequencyUnit || currentAppliance?.service_frequency_unit || "months";

            const nextServiceDate = calculateNextServiceDate(baseDate, freq, freqUnit);

            const { error } = await supabase
                .from("appliances")
                .update({
                    name: updates.name,
                    brand: updates.brand,
                    category: updates.category,
                    last_service_date: updates.lastServiceDate,
                    service_frequency: freq,
                    service_frequency_unit: freqUnit,
                    next_service_date: nextServiceDate,
                    provider_id: updates.providerId,
                    warranty_expiry: updates.warrantyExpiry,
                    installation_date: updates.installationDate,
                    provider_notifications: updates.providerNotifications,
                    purchase_price: updates.purchasePrice,
                    purchase_date: updates.purchaseDate,
                    receipt_url: updates.receiptUrl,
                    // Fix: same missing field as the insert above — without this,
                    // editing an appliance could never attach/replace a warranty doc.
                    ...(updates.warrantyUrl !== undefined && { warranty_url: updates.warrantyUrl }),
                    ...(updates.warranty_dismissed !== undefined && { warranty_dismissed: updates.warranty_dismissed }),
                    ...(updates.maintenanceTasks !== undefined && { maintenance_tasks: updates.maintenanceTasks }),
                })
                .eq("id", id)
                .eq("user_id", user.id);

            if (error) throw error;

            // Fix: editing an appliance's service interval recalculates
            // next_service_date on the appliance row, but any automated-followup
            // service_logs rows (used to drive the 30/7/1-day retention emails)
            // kept their own stale next_service_date and never resynced — so a
            // provider could still get reminded on the old schedule after the
            // interval was changed. Cascade the recalculated date to those rows.
            if (nextServiceDate) {
                const { error: logsError } = await supabase
                    .from("service_logs")
                    .update({ next_service_date: nextServiceDate })
                    .eq("appliance_id", id)
                    .eq("automated_followup", true);

                if (logsError) {
                    console.error("Failed to resync service_logs.next_service_date after interval edit:", logsError);
                }
            }
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["appliances"] });
            toast({ title: "Updated", description: "Appliance details saved." });
        },
        onError: () => {
            toast({
                title: "Error",
                description: "Failed to update appliance",
                variant: "destructive",
            });
        }
    });

    // 4. Delete Mutation
    const deleteMutation = useMutation({
        mutationFn: async (id) => {
            // First get the appliance to find the receipt_url
            const { data: appliance } = await supabase.from("appliances").select("receipt_url").eq("id", id).single();

            // If there's a receipt, try to delete it from storage
            if (appliance?.receipt_url) {
                try {
                    const urlObj = new URL(appliance.receipt_url);
                    const match = urlObj.pathname.match(/\/service-documents\/(.+)$/);
                    if (match && match[1]) {
                        await supabase.storage.from("service-documents").remove([match[1]]);
                    }
                } catch (e) {
                    console.error("Failed to delete storage file", e);
                }
            }

            // Service logs deletion handled in mutation to keep state clean
            await supabase.from("service_logs").delete().eq("appliance_id", id);
            const { error } = await supabase.from("appliances").delete().eq("id", id);
            if (error) throw error;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["appliances"] });
            toast({
                title: "Appliance Deleted",
                description: "Appliance and its service history have been removed.",
            });
        },
        onError: () => {
            toast({
                title: "Error",
                description: "Failed to delete appliance",
                variant: "destructive",
            });
        }
    });

    const getUpcomingServiceAppliances = () => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const thirtyDaysFromNow = new Date();
        thirtyDaysFromNow.setDate(today.getDate() + 30);
        thirtyDaysFromNow.setHours(23, 59, 59, 999);

        return appliances.filter((appliance) => {
            if (!appliance.next_service_date) return false;
            const nextService = new Date(appliance.next_service_date);
            nextService.setHours(0, 0, 0, 0);
            return nextService <= thirtyDaysFromNow;
        });
    };

    const getExpiringWarranties = () => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const thirtyDaysFromNow = new Date();
        thirtyDaysFromNow.setDate(today.getDate() + 30);
        thirtyDaysFromNow.setHours(23, 59, 59, 999);

        return appliances.filter((appliance) => {
            if (!appliance.warranty_expiry || appliance.warranty_dismissed) return false;
            const expiryDate = new Date(appliance.warranty_expiry);
            expiryDate.setHours(0, 0, 0, 0);
            return expiryDate <= thirtyDaysFromNow;
        });
    };

    const dismissWarranty = async (id) => {
        queryClient.setQueryData(["appliances"], (old) => {
            if (!old) return old;
            return old.map(a => a.id === id ? { ...a, warranty_dismissed: true } : a);
        });
        return updateMutation.mutateAsync({ id, updates: { warranty_dismissed: true } });
    };

    const getUpcomingSubTasks = () => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const thirtyDaysFromNow = new Date();
        thirtyDaysFromNow.setDate(today.getDate() + 30);
        thirtyDaysFromNow.setHours(23, 59, 59, 999);

        const list = [];
        appliances.forEach((appliance) => {
            const tasks = appliance.maintenance_tasks || [];
            tasks.forEach((task) => {
                if (task.next_service_date) {
                    const nextService = new Date(task.next_service_date);
                    nextService.setHours(0, 0, 0, 0);
                    if (nextService <= thirtyDaysFromNow) {
                        list.push({
                            ...task,
                            applianceId: appliance.id,
                            applianceName: appliance.name,
                            applianceBrand: appliance.brand,
                            category: appliance.category,
                            propertyId: appliance.property_id
                        });
                    }
                }
            });
        });
        return list;
    };

    return {
        appliances,
        loading,
        addAppliance: addMutation.mutateAsync,
        updateAppliance: (id, updates) => updateMutation.mutateAsync({ id, updates }),
        deleteAppliance: deleteMutation.mutateAsync,
        getUpcomingServiceAppliances,
        getUpcomingSubTasks,
        getExpiringWarranties,
        dismissWarranty,
        refetch,
    };
};
